import ErrorHelper from "./ErrorWrapper";
import Flex from "./Flex";
import { useFieldLock } from "./ReadOnlyFields";
import { MultiSelectPopover } from "./mask-multi-select/MultiSelectPopover";
import { MultiSelectTrigger } from "./mask-multi-select/MultiSelectTrigger";
import { MaskMultiSelectScrollProps, MData } from "./mask-multi-select/types";
import { useMaskMultiSelectScroll } from "./mask-multi-select/use-mask-multi-select-scroll";

/**
 * MaskMultiSelectScroll — {@link MaskMultiSelect} with infinite-scroll options.
 *
 * The caller owns `pagination`/`setPagination` and feeds each page's `options`
 * plus `totalElements`; the popup accumulates pages and requests the next one
 * when the options list nears its bottom. `loading` renders the inline spinner.
 *
 * @author [Rajkumar Rathod]
 */
export const MaskMultiSelectScroll = <T extends MData>({
  label,
  required,
  flexProps,
  placeholder = "Select Option from Dropdown",
  options,
  error,
  name,
  handleBlur,
  value = [],
  onChange,
  readOnly,
  disabled,
  onSearch,
  limitTags = 1,
  optionWithSelectAll = false,
  renderCustomLabel,
  pagination,
  setPagination,
  totalElements,
  loading,
}: MaskMultiSelectScrollProps<T>) => {
  const lock = useFieldLock(disabled || readOnly);
  const controller = useMaskMultiSelectScroll<T>({
    options,
    value,
    onChange,
    onSearch,
    handleBlur,
    name,
    disabled: lock.disabled,
    readOnly: lock.readOnly,
    pagination,
    setPagination,
    totalElements,
    loading,
  });

  return (
    <form autoComplete="off">
      <Flex {...flexProps} flexDirection={flexProps?.flexDirection ?? "column"}>
        <ErrorHelper error={error}>
          <MultiSelectTrigger
            anchorRef={controller.anchorRef}
            label={label}
            required={required}
            placeholder={placeholder}
            name={name}
            error={error}
            disabled={lock.disabled}
            readOnly={lock.readOnly}
            open={controller.open}
            value={value}
            idKey={controller.idKey}
            labelKey={controller.labelKey}
            limitTags={limitTags}
            onTogglePaper={
              controller.open ? controller.closePopup : controller.openPopup
            }
          />

          <MultiSelectPopover
            controller={controller}
            optionWithSelectAll={optionWithSelectAll}
            renderCustomLabel={renderCustomLabel}
            onOptionsScroll={controller.onScroll}
            loadingMore={controller.loadingMore}
          />
        </ErrorHelper>
      </Flex>
    </form>
  );
};

export default MaskMultiSelectScroll;
